import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './AuthPage.css';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import { TERMS_VERSION } from '../App';
import { isUsernameAllowed } from '../lib/usernameFilter';
import { isDisposableEmail, isTestEmail } from '../utils/emailValidation';

// PRD-037 Task 2.4: Validation rules with strengthened password requirements
const validationRules = {
  email: {
    required: 'Email is required',
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  username: {
    required: 'Username is required',
    minLength: 3,
    maxLength: 20,
    pattern: /^[a-zA-Z0-9_-]+$/,
    message: 'Username must be 3-20 characters and contain only letters, numbers, underscores, and hyphens'
  },
  password: {
    required: 'Password is required',
    minLength: 12,
    // eslint-disable-next-line no-useless-escape
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
    message: 'Password must be at least 12 characters with uppercase, lowercase, number, and special character'
  }
};

const fieldLabels = { email: 'Email', username: 'Username', password: 'Password', confirmPassword: 'Confirm password' };

// PRD-037 Task 2.4: Password strength indicator
const getPasswordStrength = (password) => {
  if (!password) return { strength: 0, label: '', color: '' };
  let strength = 0;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  // eslint-disable-next-line no-useless-escape
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength++;
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['#ff4444', '#ff8800', '#ffaa00', '#00aa00', '#008800'];
  return { strength, label: labels[strength - 1] || '', color: colors[strength - 1] || '' };
};

// Clear only auth-related localStorage keys
const clearAuthStorage = () => {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('sb-') || key === 'tempUsername' || key === 'supabase.auth.token')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
  sessionStorage.clear();
};

const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser, login } = useAuth();

  // Single source of truth: derive tab from URL path
  const activeTab = location.pathname === '/register' ? 'register' : 'login';
  const switchTab = (tab) => {
    if (tab === 'register') setRegisterStep(1);
    navigate(tab === 'register' ? '/register' : '/login', { replace: true });
  };

  const [showMoreOptions, setShowMoreOptions] = useState(false);

  // Social proof: fetch citizen count once (anon-safe RPC)
  const [citizenCount, setCitizenCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc('get_public_citizen_count')
      .then(({ data }) => { if (!cancelled && data) setCitizenCount(data); });
    return () => { cancelled = true; };
  }, []);

  // Login state
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginErrors, setLoginErrors] = useState({});
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [magicLinkCooldown, setMagicLinkCooldown] = useState(false);

  // Register state
  const [registerData, setRegisterData] = useState({
    email: '', username: '', password: '', confirmPassword: ''
  });
  const [registerErrors, setRegisterErrors] = useState({});
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  const [registerStep, setRegisterStep] = useState(1);
  const [tosAccepted, setTosAccepted] = useState(false);
  // PRD-098: Honeypot field for bot detection
  const [honeypot, setHoneypot] = useState('');

  // Already logged in
  if (isAuthenticated) {
    return (
      <div className="page-container auth-page auth-page-wrapper">
        <div className="auth-form-container card-style" style={{ width: '100%' }}>
          <h2>Welcome Back</h2>
          <p className="success-message">You are logged in as {currentUser?.username || currentUser?.email}!</p>
          <button onClick={() => navigate('/')} className="button">Go to Homepage</button>
        </div>
      </div>
    );
  }

  // ---- LOGIN HANDLERS ----
  const validateLoginField = (name, value) => {
    if (!value) return `${fieldLabels[name] || name} is required`;
    if (name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    setLoginErrors(prev => ({ ...prev, [name]: validateLoginField(name, value) }));
    if (loginSuccess) setLoginSuccess('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErrors({}); setLoginSuccess('');
    const newErrors = {};
    Object.keys(loginData).forEach(field => {
      const error = validateLoginField(field, loginData[field]);
      if (error) newErrors[field] = error;
    });
    if (Object.keys(newErrors).length > 0) { setLoginErrors(newErrors); return; }

    setLoginLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginData.email, password: loginData.password
      });
      // PRD-037 Task 3.1: Clear password from state immediately after submission
      setLoginData(prev => ({ ...prev, password: '' }));
      if (error) {
        const msg = String(error.message || '').toLowerCase();
        if (msg.includes('invalid login credentials')) setLoginErrors({ general: 'Invalid email or password.' });
        else if (msg.includes('email not confirmed')) setLoginErrors({ general: 'Please confirm your email before logging in.' });
        else setLoginErrors({ general: 'Login failed. Please check your credentials and try again.' });
        return;
      }
      setLoginSuccess('Login successful!');
      const redirectTo = new URLSearchParams(location.search).get('redirect');
      navigate(redirectTo && redirectTo.startsWith('/') ? redirectTo : '/', { replace: true });
    } catch {
      setLoginData(prev => ({ ...prev, password: '' }));
      setLoginErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally { setLoginLoading(false); }
  };

  const sendMagicLink = async () => {
    // Validate email format before sending
    if (!loginData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginData.email)) {
      setLoginErrors({ email: 'Please enter a valid email address' });
      return;
    }
    setLoginErrors({}); setLoginSuccess(''); setLoginLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ email: loginData.email, options: { shouldCreateUser: false } });
      if (error) throw error;
      setOtpSent(true);
      setLoginSuccess('Magic link sent. Check your email.');
      // 60-second cooldown to prevent spam
      setMagicLinkCooldown(true);
      setTimeout(() => setMagicLinkCooldown(false), 60000);
    } catch (e) {
      const msg = String(e.message || '').toLowerCase();
      if (msg.includes('rate limit') || msg.includes('429')) {
        setLoginErrors({ general: 'Too many attempts. Please wait before trying again.' });
      } else {
        setLoginErrors({ general: 'Failed to send magic link. Please try again.' });
      }
    } finally { setLoginLoading(false); }
  };

  const resetSession = async () => {
    try { await supabase.auth.signOut(); } catch {}
    try { clearAuthStorage(); } catch {}
    window.location.reload();
  };

  // ---- REGISTER HANDLERS ----
  const validateRegField = (name, value) => {
    const rules = validationRules[name];
    if (!rules) return '';
    if (rules.required && !value) return rules.required;
    if (rules.minLength && value.length < rules.minLength) return `${fieldLabels[name] || name} must be at least ${rules.minLength} characters`;
    if (rules.maxLength && value.length > rules.maxLength) return `${fieldLabels[name] || name} must be no more than ${rules.maxLength} characters`;
    if (rules.pattern && !rules.pattern.test(value)) return rules.message;
    if (name === 'username' && value.length >= 3) {
      const filterResult = isUsernameAllowed(value);
      if (!filterResult.allowed) return filterResult.reason;
    }
    return '';
  };

  const handleRegChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({ ...prev, [name]: value }));
    const error = validateRegField(name, value);
    setRegisterErrors(prev => ({ ...prev, [name]: error }));
    // Real-time confirm-password cross-validation
    if (name === 'confirmPassword' && value && value !== registerData.password) {
      setRegisterErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
    } else if (name === 'password' && registerData.confirmPassword && value !== registerData.confirmPassword) {
      setRegisterErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
    } else if (name === 'confirmPassword' && value === registerData.password) {
      setRegisterErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
    if (registerSuccess) setRegisterSuccess('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterErrors({}); setRegisterSuccess('');

    // PRD-098: Honeypot check - bots fill hidden fields, humans don't
    if (honeypot) {
      // Fake success — mimic real registration timing so bots can't detect the honeypot
      setRegisterLoading(true);
      await new Promise(r => setTimeout(r, 800 + Math.random() * 1200));
      setRegisterLoading(false);
      setRegisterSuccess('Registration successful! Please check your email to confirm your account before logging in.');
      setRegisterData({ email: '', username: '', password: '', confirmPassword: '' });
      setTimeout(() => { switchTab('login'); }, 3000);
      return;
    }

    const newErrors = {};
    Object.keys(validationRules).forEach(field => {
      const error = validateRegField(field, registerData[field]);
      if (error) newErrors[field] = error;
    });
    if (registerData.password !== registerData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!tosAccepted) {
      newErrors.tos = 'You must agree to the Terms of Service, Privacy Policy, and Community Rules';
    }
    if (Object.keys(newErrors).length > 0) { setRegisterErrors(newErrors); return; }

    setRegisterLoading(true);
    try {
      // PRD-098: Disposable email blocklist check (skip for test/admin emails)
      if (!isTestEmail(registerData.email)) {
        const disposable = await isDisposableEmail(registerData.email);
        if (disposable) {
          setRegisterErrors({ email: 'Please use a permanent email address. Temporary email services are not accepted.' });
          setRegisterLoading(false);
          return;
        }
      }
      // PRD-037: Only send username and terms metadata; role/tier are hardcoded by DB trigger
      const { data, error } = await supabase.auth.signUp({
        email: registerData.email,
        password: registerData.password,
        options: {
          data: {
            username: registerData.username,
            terms_version: TERMS_VERSION,
            terms_accepted_at: new Date().toISOString()
          }
        }
      });
      // PRD-037 Task 3.1: Clear password from state immediately after submission
      setRegisterData(prev => ({ ...prev, password: '', confirmPassword: '' }));
      if (error) {
        const errMsg = (error.message || '').toLowerCase();
        if (errMsg.includes('rate limit') || error.status === 429) setRegisterErrors({ general: 'Too many attempts. Please wait 1 minute before trying again.' });
        else if (errMsg.includes('already registered') || errMsg.includes('user already exists')) setRegisterErrors({ email: 'This email is already registered' });
        else if (errMsg.includes('sending confirmation')) setRegisterErrors({ general: 'Unable to send confirmation email. Please try again or contact support.' });
        else if (errMsg.includes('signal is aborted') || errMsg.includes('abort') || errMsg.includes('timed out') || errMsg.includes('timeout') || error.status === 504) setRegisterErrors({ general: 'The server took too long to respond. Please wait a moment and try again.' });
        else if (errMsg.includes('password')) setRegisterErrors({ password: 'Password does not meet requirements' });
        else setRegisterErrors({ general: 'An unexpected error occurred. Please try again.' });
        return;
      }
      const userId = data?.user?.id;
      if (!userId) { setRegisterErrors({ general: 'Could not determine user ID for profile creation.' }); return; }
      localStorage.removeItem('tempUsername');
      localStorage.setItem('tempUsername', registerData.username);

      if (data.user && !data.user.email_confirmed_at) {
        setRegisterSuccess('Registration successful! Please check your email to confirm your account before logging in.');
        setRegisterData({ email: '', username: '', password: '', confirmPassword: '' });
        setTimeout(() => { switchTab('login'); }, 3000);
      } else if (data.user && data.session) {
        setRegisterSuccess('Registration successful! Logging you in...');
        login({ id: userId, username: registerData.username, email: registerData.email, role: 'user' }, data.session.access_token);
        navigate('/');
      }
    } catch {
      // PRD-037 Task 3.1: Clear passwords on error
      setRegisterData(prev => ({ ...prev, password: '', confirmPassword: '' }));
      setRegisterErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally { setRegisterLoading(false); }
  };

  // Only compute password strength when register tab is active
  const passwordStrength = activeTab === 'register' ? getPasswordStrength(registerData.password) : { strength: 0, label: '', color: '' };

  return (
    <div className="page-container auth-page auth-page-wrapper">
      <div className="auth-form-container card-style" style={{ width: '100%' }}>
        {/* Tab Header - WAI-ARIA tab pattern */}
        <div className="auth-tabs" role="tablist" aria-label="Account access">
          <button
            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => switchTab('login')}
            type="button"
            role="tab"
            aria-selected={activeTab === 'login'}
            aria-controls="auth-panel-login"
            id="auth-tab-login"
          >
            Sign In
          </button>
          <button
            className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => switchTab('register')}
            type="button"
            role="tab"
            aria-selected={activeTab === 'register'}
            aria-controls="auth-panel-register"
            id="auth-tab-register"
          >
            Sign Up
          </button>
        </div>

        {/* ---- SIGN IN TAB ---- */}
        {activeTab === 'login' && (
          <div className="auth-tab-content" role="tabpanel" id="auth-panel-login" aria-labelledby="auth-tab-login">
            <p className="auth-subtitle">Welcome back, citizen</p>
            <form onSubmit={handleLogin} noValidate>
              <div className="form-group">
                <label htmlFor="login-email">Email Address</label>
                <input type="email" id="login-email" name="email" value={loginData.email} onChange={handleLoginChange} placeholder="Enter your email address" className={loginErrors.email ? 'error' : ''} required autoComplete="email" />
                {loginErrors.email && <span className="error-text">{loginErrors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="login-password">Password</label>
                <div className="password-input-container">
                  <input type={showLoginPassword ? 'text' : 'password'} id="login-password" name="password" value={loginData.password} onChange={handleLoginChange} placeholder="Enter your password" className={loginErrors.password ? 'error' : ''} required autoComplete="current-password" />
                  <button type="button" className="password-toggle" onClick={() => setShowLoginPassword(!showLoginPassword)} aria-label={showLoginPassword ? 'Hide password' : 'Show password'}>
                    {showLoginPassword ? '\u{1F441}\uFE0F' : '\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F'}
                  </button>
                </div>
                {loginErrors.password && <span className="error-text">{loginErrors.password}</span>}
              </div>
              {loginErrors.general && <p className="error-message">{loginErrors.general}</p>}
              {loginSuccess && <p className="success-message">{loginSuccess}</p>}
              <button type="submit" className="button" disabled={loginLoading}>
                {loginLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <p className="auth-switch-message" style={{ marginTop: '12px' }}>
              <Link to="/forgot-password">Forgot your password?</Link>
            </p>

            {/* Collapsible more options */}
            <div className="auth-more-options">
              <button type="button" className="auth-more-toggle" onClick={() => setShowMoreOptions(!showMoreOptions)} aria-expanded={showMoreOptions}>
                {showMoreOptions ? 'Less options' : 'More options'}
                <span className={`auth-more-arrow ${showMoreOptions ? 'open' : ''}`}>&#9662;</span>
              </button>
              {showMoreOptions && (
                <div className="auth-more-content">
                  <button type="button" className="button-secondary" onClick={sendMagicLink} disabled={!loginData.email || loginLoading || magicLinkCooldown} style={{ width: '100%', marginBottom: '8px' }}>
                    {magicLinkCooldown ? 'Magic link sent (wait 60s)' : otpSent ? 'Resend Magic Link' : 'Send Magic Link'}
                  </button>
                  <button type="button" className="button-secondary" onClick={resetSession} style={{ width: '100%' }}>
                    Reset Session
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---- CREATE ACCOUNT TAB ---- */}
        {activeTab === 'register' && (
          <div className="auth-tab-content" role="tabpanel" id="auth-panel-register" aria-labelledby="auth-tab-register">

            {/* ---- STEP 1: Email ---- */}
            {registerStep === 1 && (
              <>
                <p className="auth-subtitle">Your Star Citizen trading companion</p>
                <ul className="auth-value-props">
                  <li>Browse 2,600+ items across 115 shops</li>
                  <li>Track commodity prices and plan trade routes</li>
                  <li>Find crew and trade with other citizens</li>
                </ul>
                {citizenCount && citizenCount > 10 && (
                  <p className="auth-social-proof">Join {citizenCount.toLocaleString()}+ citizens already on board</p>
                )}
                <div className="form-group">
                  <label htmlFor="reg-email">Email Address</label>
                  <input type="email" id="reg-email" name="email" value={registerData.email} onChange={handleRegChange} placeholder="your.email@example.com" className={registerErrors.email ? 'error' : ''} required autoComplete="email" />
                  {registerErrors.email && <span className="error-text">{registerErrors.email}</span>}
                </div>
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    const emailError = validateRegField('email', registerData.email);
                    if (emailError) {
                      setRegisterErrors(prev => ({ ...prev, email: emailError }));
                      return;
                    }
                    setRegisterErrors({});
                    setRegisterStep(2);
                  }}
                >
                  Continue
                </button>
                <div className="terms-notice">
                  <small>You'll be asked to agree to our <Link to="/terms">Terms of Service</Link>, <Link to="/privacy">Privacy Policy</Link>, and <Link to="/community-rules">Community Rules</Link> in the next step.</small>
                </div>
              </>
            )}

            {/* ---- STEP 2: Username + Password ---- */}
            {registerStep === 2 && (
              <>
                <div className="auth-step-header">
                  <button type="button" className="auth-back-button" onClick={() => setRegisterStep(1)} aria-label="Go back to email">
                    &#8592; Back
                  </button>
                  <p className="auth-step-email">{registerData.email}</p>
                </div>
                <form onSubmit={handleRegister} noValidate>
                  {/* PRD-098: Honeypot field - invisible to humans, filled by bots */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                    <label htmlFor="company_website">Website</label>
                    <input
                      type="text"
                      id="company_website"
                      name="company_website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={e => setHoneypot(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="reg-username">Username</label>
                    <input type="text" id="reg-username" name="username" value={registerData.username} onChange={handleRegChange} placeholder="Choose a username" className={registerErrors.username ? 'error' : ''} required autoComplete="username" />
                    {registerErrors.username && <span className="error-text">{registerErrors.username}</span>}
                    <small className="help-text">This is your public display name — don't use your Star Citizen handle. You can verify your SC handle separately after signing up.</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="reg-password">Password</label>
                    <div className="password-input-container">
                      <input type={showRegPassword ? 'text' : 'password'} id="reg-password" name="password" value={registerData.password} onChange={handleRegChange} placeholder="Create a password" className={registerErrors.password ? 'error' : ''} required autoComplete="new-password" />
                      <button type="button" className="password-toggle" onClick={() => setShowRegPassword(!showRegPassword)} aria-label={showRegPassword ? 'Hide password' : 'Show password'}>
                        {showRegPassword ? '\u{1F441}\uFE0F' : '\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F'}
                      </button>
                    </div>
                    {registerErrors.password && <span className="error-text">{registerErrors.password}</span>}
                    {registerData.password && (
                      <div className="password-strength">
                        <div className="strength-bar" aria-label={`Password strength: ${passwordStrength.label}`}>
                          {[1, 2, 3, 4, 5].map(level => (
                            <div key={level} className={`strength-segment ${passwordStrength.strength >= level ? 'filled' : ''}`} style={{ backgroundColor: passwordStrength.strength >= level ? passwordStrength.color : '#333' }} />
                          ))}
                        </div>
                        <span className="strength-label" style={{ color: passwordStrength.color }}>{passwordStrength.label}</span>
                      </div>
                    )}
                    {registerData.password && (
                      <small className="help-text">Min 12 characters: uppercase, lowercase, number, and special character</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="reg-confirmPassword">Confirm Password</label>
                    <div className="password-input-container">
                      <input type={showRegConfirmPassword ? 'text' : 'password'} id="reg-confirmPassword" name="confirmPassword" value={registerData.confirmPassword} onChange={handleRegChange} placeholder="Confirm your password" className={registerErrors.confirmPassword ? 'error' : ''} required autoComplete="new-password" />
                      <button type="button" className="password-toggle" onClick={() => setShowRegConfirmPassword(!showRegConfirmPassword)} aria-label={showRegConfirmPassword ? 'Hide password' : 'Show password'}>
                        {showRegConfirmPassword ? '\u{1F441}\uFE0F' : '\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F'}
                      </button>
                    </div>
                    {registerErrors.confirmPassword && <span className="error-text">{registerErrors.confirmPassword}</span>}
                  </div>

                  <div className="form-group" style={{ marginTop: '8px' }}>
                    <label className="auth-tos-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={tosAccepted}
                        onChange={e => {
                          setTosAccepted(e.target.checked);
                          if (e.target.checked) setRegisterErrors(prev => ({ ...prev, tos: '' }));
                        }}
                        style={{ marginTop: '3px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '13px', lineHeight: '1.4' }}>
                        I agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>, <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>, and <Link to="/community-rules" target="_blank" rel="noopener noreferrer">Community Rules</Link>.
                      </span>
                    </label>
                    {registerErrors.tos && <span className="error-text">{registerErrors.tos}</span>}
                  </div>

                  {registerErrors.general && <p className="error-message">{registerErrors.general}</p>}
                  {registerSuccess && <p className="success-message">{registerSuccess}</p>}

                  <button type="submit" className="button" disabled={registerLoading || !tosAccepted}>
                    {registerLoading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </form>

              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
